import {
  endOfToday,
  format,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
  subWeeks,
} from 'date-fns'
import { ArrowDownRight, ArrowUpRight, BarChart3 } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import { type AnalyticsParams, useEventAnalytics } from '~/entities/stats'
import * as m from '~/paraglide/messages'
import { getLocale } from '~/paraglide/runtime.js'
import { cn } from '~/shared/lib/utils'
import { METRIC_COLORS } from '~/shared/styles/chart-palette'
import { StackedBars, type StackedBarsSegment } from '~/shared/ui/stacked-bars/stacked-bars'
import type { StackedBarsPoint } from '~/shared/ui/stacked-bars/stacked-bars-data'

type Granularity = 'day' | 'week' | 'month' | 'all'
type ResolvedGranularity = 'day' | 'week' | 'month'
type MetricKey = 'views' | 'interactions' | 'likes' | 'impressions' | 'checkIns'
type ChartKey = Exclude<MetricKey, 'likes'>

const EXTRA_METRIC_KEYS: MetricKey[] = ['impressions', 'interactions', 'likes']
const EMPTY_TOTALS: Record<MetricKey, number> = {
  views: 0,
  interactions: 0,
  likes: 0,
  impressions: 0,
  checkIns: 0,
}

function metricLabel(key: MetricKey): string {
  if (key === 'views') return m.views()
  if (key === 'interactions') return m.interactions()
  if (key === 'impressions') return m.impressions()
  if (key === 'checkIns') return m.liveRoomFunnelJoins()
  return m.likes()
}

function chartSegments(): StackedBarsSegment<ChartKey>[] {
  return (['views', 'interactions', 'checkIns', 'impressions'] as const).map((key) => ({
    key,
    label: metricLabel(key),
    color: METRIC_COLORS[key],
  }))
}

function granularityOptions(): { value: Granularity; label: string }[] {
  return [
    { value: 'day', label: m.chartPeriodDay() },
    { value: 'week', label: m.chartPeriodWeek() },
    { value: 'month', label: m.chartPeriodMonth() },
    { value: 'all', label: m.chartPeriodAll() },
  ]
}

/** Current + previous equal-length windows for a granularity, as YYYY-MM-DD. */
function windowsFor(granularity: ResolvedGranularity) {
  const today = endOfToday()
  const iso = (d: Date) => format(d, 'yyyy-MM-dd')
  if (granularity === 'week') {
    const curFrom = startOfWeek(subWeeks(today, 11), { weekStartsOn: 1 })
    const prevTo = subDays(curFrom, 1)
    return {
      cur: { from: iso(curFrom), to: iso(today) },
      prev: { from: iso(subWeeks(curFrom, 12)), to: iso(prevTo) },
    }
  }
  if (granularity === 'month') {
    const curFrom = startOfMonth(subMonths(today, 11))
    const prevTo = subDays(curFrom, 1)
    return {
      cur: { from: iso(curFrom), to: iso(today) },
      prev: { from: iso(subMonths(curFrom, 12)), to: iso(prevTo) },
    }
  }
  const curFrom = subDays(today, 29)
  const prevTo = subDays(curFrom, 1)
  return {
    cur: { from: iso(curFrom), to: iso(today) },
    prev: { from: iso(subDays(prevTo, 29)), to: iso(prevTo) },
  }
}

function deltaPct(current: number, previous: number): number | null {
  if (previous === 0) return current === 0 ? 0 : null
  return ((current - previous) / previous) * 100
}

export function EventAnalyticsChart({ eventId }: { eventId: string }) {
  const { data: business } = useCurrentBusiness()
  const locale = getLocale()
  const [granularity, setGranularity] = useState<Granularity>('all')

  const businessId = business?.id ?? ''
  const isAll = granularity === 'all'
  const win = useMemo(() => (granularity === 'all' ? null : windowsFor(granularity)), [granularity])
  const curParams: AnalyticsParams = win ? { ...win.cur, granularity } : { granularity: 'all' }
  const prevParams: AnalyticsParams = win ? { ...win.prev, granularity } : { granularity: 'all' }
  const cur = useEventAnalytics(businessId, eventId, curParams)
  const prev = useEventAnalytics(businessId, eventId, prevParams, {
    enabled: !isAll,
  })

  const series: StackedBarsPoint<ChartKey>[] = (cur.data?.series ?? []).map((bucket) => ({
    date: bucket.bucket,
    values: {
      views: bucket.views,
      interactions: bucket.interactions,
      checkIns: bucket.checkIns,
      impressions: bucket.impressions,
    },
  }))

  const loading = cur.isLoading || !business
  const totals = cur.data?.totals ?? EMPTY_TOTALS
  const prevTotals = prev.data?.totals ?? EMPTY_TOTALS
  const viewsChange =
    isAll || prevTotals.views === 0 ? null : (totals.views - prevTotals.views) / prevTotals.views

  const extraKeys = EXTRA_METRIC_KEYS.filter((key) => totals[key] > 0 || prevTotals[key] > 0)

  return (
    <div className="space-y-4">
      <StackedBars
        series={series}
        segments={chartSegments()}
        bucket={cur.data?.granularity ?? 'day'}
        title={m.eventAnalytics()}
        icon={<BarChart3 className="h-4 w-4 text-muted-foreground" />}
        headline={{
          value: totals.views,
          label: m.views(),
          change: viewsChange,
          changeLabel: m.vsPreviousPeriod(),
        }}
        periods={granularityOptions()}
        period={granularity}
        onPeriodChange={setGranularity}
        loading={loading}
        emptyText={m.noAnalyticsDataAvailable()}
        height={200}
        legend
        locale={locale}
      />

      {!loading && extraKeys.length > 0 && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {extraKeys.map((key) => (
            <StatTile
              key={key}
              label={metricLabel(key)}
              value={totals[key]}
              previous={prevTotals[key]}
              color={METRIC_COLORS[key]}
              showDelta={!isAll}
              locale={locale}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function StatTile({
  label,
  value,
  previous,
  color,
  showDelta,
  locale,
}: {
  label: string
  value: number
  previous: number
  color: string
  showDelta: boolean
  locale: string
}) {
  const pct = showDelta ? deltaPct(value, previous) : null
  const up = pct !== null && pct >= 0
  return (
    <div className="rounded-lg border p-3">
      <div className="flex items-center gap-1.5">
        <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: color }} />
        <span className="truncate text-muted-foreground text-xs">{label}</span>
      </div>
      <p className="mt-1 font-serif font-semibold text-2xl tabular-nums">
        {value.toLocaleString(locale)}
      </p>
      {pct !== null && Math.round(pct) !== 0 && (
        <span
          className={cn(
            'mt-0.5 inline-flex items-center gap-0.5 text-xs tabular-nums',
            up ? 'text-emerald-600 dark:text-emerald-500' : 'text-rose-600 dark:text-rose-500',
          )}
        >
          {up ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
          {Math.abs(Math.round(pct))}%
        </span>
      )}
    </div>
  )
}
